import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import type { PortfolioFluxPoint } from '../../domain/portfolioFlux';
import ChartPlaceholder from './ChartPlaceholder';
import { createCompactChartBase } from './chartConfigUtils';

interface PortfolioFluxChartProps {
    readonly data: readonly PortfolioFluxPoint[];
    readonly loading?: boolean;
    readonly currency: 'EUR' | 'LEI';
    readonly referenceValue?: number;
}

function PortfolioFluxChart({
    data,
    loading = false,
    currency,
    referenceValue,
}: PortfolioFluxChartProps): JSX.Element {
    const { t } = useTranslation();
    const hasCurrentData = data.some(point => point.current !== null);

    if (loading || !hasCurrentData) {
        return <ChartPlaceholder loading={loading} hasData={hasCurrentData} />;
    }

    const currencyLabel = currency === 'EUR' ? 'EUR' : 'RON';
    const base = createCompactChartBase(
        'line',
        data.map(point => point.date),
        t('statistics.portfolioFlux.amount', {
            currency: currencyLabel,
            defaultValue: `Active + Passive (${currencyLabel})`,
        }),
    );
    const options: Highcharts.Options = {
        ...base,
        series: [
            {
                type: 'line',
                name: t('statistics.portfolioFlux.current', 'Selected period'),
                data: data.map(point => point.current),
                color: '#1890ff',
            },
            {
                type: 'line',
                name: t('statistics.portfolioFlux.thirtyDaysEarlier', '30 days earlier'),
                data: data.map(point => point.thirtyDaysEarlier),
                color: '#faad14',
            },
            {
                type: 'line',
                name: t('statistics.portfolioFlux.previousYear', 'Same period last year'),
                data: data.map(point => point.previousYear),
                color: '#722ed1',
            },
        ],
        yAxis: {
            ...base.yAxis,
            plotLines:
                referenceValue && referenceValue > 0
                    ? [{ value: referenceValue, color: '#ff4d4f', width: 2, dashStyle: 'Dash' }]
                    : [],
        },
        legend: { enabled: true },
        credits: { enabled: false },
        tooltip: { shared: true, valueSuffix: ` ${currencyLabel}`, valueDecimals: 0 },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default PortfolioFluxChart;
